import { FormSelect, Modal, ModalSizeEnum, PrimaryButton, SecondaryButton, TimePicker, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { FUNNEL_EMAIL_API, createFunnelEmail } from 'modules/funnels/funnel/components/email-tab/api/funnel-email-api';
import { EventTypeEnum } from 'modules/funnels/funnel/components/email-tab/enums/funnel-emails-enum';
import { useFunnelEmailInfoSender } from 'modules/funnels/funnel/components/email-tab/hooks/use-funnel-emails';
import { FunnelEmailsErrorInterface } from 'modules/funnels/funnel/components/email-tab/types/funnel-emails-error-itnerface';
import { FunnelEmailEditInterface, FunnelEmailsApiInterface } from 'modules/funnels/funnel/components/email-tab/types/funnel-emails-interface';
import React, { useCallback, useEffect, useState } from 'react';
import DashboardMailEditor from 'shared/components/dashboard-mail-editor';
import { UnsavedEmailsEnum } from 'shared/components/dashboard-mail-editor/enums/unsaved-emails-enum';
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email';
import { AttachmentFileInterface, EmailBaseInterface, EmailErrorType, UnsavedAttachmentFileInterface } from 'shared/components/dashboard-mail-editor/types/email-interface';
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data';
import TimeDelayPicker, { TimeDelayPickerProps } from 'shared/components/date-picker/time-delay-picker';
import { TimeType, TimeTypeState } from 'shared/components/date-picker/time-delay-picker/constants';
import { convertTimeValueToSeconds } from 'shared/components/date-picker/time-delay-picker/utils';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUser from 'shared/hooks/use-user';
import { PaginationInterface } from 'shared/types/pagination-interface';
import { isValidEmail } from 'shared/utils/email-validation';
const defaultValues: EmailBaseInterface = {
  body: '',
  subject: '',
  previewText: '',
  fromName: '',
  fromEmail: ''
};
const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  attachments: '',
  common: [],
  delay: ''
};
interface FunnelEmailCreateProps {
  funnelStepId?: number;
  emails?: FunnelEmailsApiInterface;
  setPagination: React.Dispatch<React.SetStateAction<PaginationInterface>>;
}
const FunnelEmailCreate = ({
  emails,
  funnelStepId,
  setPagination
}: FunnelEmailCreateProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const [opened, setOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<EventTypeEnum>(EventTypeEnum.registration_to_webinar);
  const [chosenTimeDelay, setChosenTimeDelay] = useState<number>(0);
  const [chosenSendingEmailTime, setChosenSendingEmailTime] = useState<number | null>(null);
  const [selectedTimeBeforeAfter, setSelectedTimeBeforeAfter] = useState<TimeTypeState | undefined>(TimeTypeState.after);
  const [selectedTimeType, setSelectedTimeType] = useState<TimeType>(TimeType.MINUTES);
  const [tempState, setTempState] = useState<EmailBaseInterface>(defaultValues);
  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors);
  const [attachmentsTempState, setAttachmentsTempState] = useState<(AttachmentFileInterface | UnsavedAttachmentFileInterface)[]>([]);
  const {
    data: infoSender
  } = useFunnelEmailInfoSender(funnelStepId);
  const updateData = useUpdateData({
    api: `${FUNNEL_EMAIL_API}/${funnelStepId}/webinar/emails`,
    customApi: `${FUNNEL_EMAIL_API}/${funnelStepId}/webinar/emails`,
    constantApi: [`${FUNNEL_EMAIL_API}/${funnelStepId}/webinar/emails/sender-info`],
    setPagination
  });
  const eventSelector = [{
    caption: t('dashboard.funnel.emails_registration_to_webinar'),
    id: EventTypeEnum.registration_to_webinar
  }, {
    caption: t('dashboard.funnel.emails_webinar_start'),
    id: EventTypeEnum.webinar_start
  }];
  useEffect(() => {
    setChosenSendingEmailTime(null);
  }, [selectedTimeBeforeAfter]);
  useEffect(() => {
    setSelectedTimeBeforeAfter(TimeTypeState.after);
    setChosenSendingEmailTime(null);
    setChosenTimeDelay(0);
  }, [selectedCourse]);
  useEffect(() => {
    if (infoSender) {
      setTempState(prev => ({
        ...prev,
        fromEmail: infoSender.senderAddress,
        fromName: infoSender.senderName
      }));
    }
  }, [infoSender, opened]);
  const renderTime = () => {
    return <>
        <FormSelect withoutCloseIcon label={t('global.event_caption')} data={eventSelector} value={selectedCourse} onChange={data => data && setSelectedCourse(data)} />
        <TimeDelayPicker required selectedTimeType={selectedTimeType} selectedTimeBeforeAfter={selectedTimeBeforeAfter} selectedTimeValue={chosenTimeDelay} className={'pt-5'} classNameInputInteger={'lg:min-w-[40px]'} label={t('dashboard.funnel.emails_webinar.delay')} onChange={onChooseTimeDelay} error={errors.delay} withTimeAfterBeforeSelect={selectedCourse === EventTypeEnum.webinar_start} />
        {(selectedCourse === EventTypeEnum.registration_to_webinar || selectedTimeBeforeAfter === TimeTypeState.after) && <TimePicker wrapperClassName={'pt-5'} label={t('dashboard.funnel.emails_webinar.send_at_certain_time')} seconds={chosenSendingEmailTime} onChangeSeconds={setChosenSendingEmailTime} userDateFormat={user?.dateFormat} userTimeFormat={user?.timeFormat} />}
      </>;
  };
  const onCreate = async () => {
    try {
      if (tempState && funnelStepId) {
        setErrors(defaultErrors);
        if (!isValidEmail(tempState.fromEmail)) {
          return setErrors(prev_2 => ({
            ...prev_2,
            fromEmail: t('dashboard.validation.email_not_valid')
          }));
        }
        setIsFetching(true);
        const newData: FunnelEmailEditInterface = {
          mailing: {
            currentEmail: getMailingUpdateData({
              ...tempState,
              body: tempState.body
            }, attachmentsTempState)
          },
          event: selectedCourse,
          delay: convertTimeValueToSeconds(chosenTimeDelay, selectedTimeType, selectedTimeBeforeAfter),
          certainTime: chosenSendingEmailTime
        };
        await createFunnelEmail(funnelStepId, newData);
        await updateData(true, emails);
        await removeDbRecord();
        onClose();
        setIsFetching(false);
        toast.success(t('global.changes_saved'));
      }
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        const funnelEmailSettingsErrors = e.errors as unknown as FunnelEmailsErrorInterface;
        if (funnelEmailSettingsErrors) {
          const fieldErrors = !Array.isArray(funnelEmailSettingsErrors.fields) ? funnelEmailSettingsErrors.fields : undefined;
          if (fieldErrors) {
            setErrors(prev_0 => ({
              ...prev_0,
              body: fieldErrors.mailing?.currentEmail?.body?.join(''),
              fromEmail: fieldErrors.mailing?.currentEmail?.fromEmail?.join(''),
              fromName: fieldErrors.mailing?.currentEmail?.fromName?.join(''),
              subject: fieldErrors.mailing?.currentEmail?.subject?.join(''),
              previewText: fieldErrors.mailing?.currentEmail?.previewText?.join(''),
              delay: fieldErrors.delay?.join('')
            }));
          }
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev_1 => {
            return {
              ...prev_1,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const onChooseTimeDelay = useCallback<TimeDelayPickerProps['onChange']>((timeDelay, timeType, timeBeforeAfter) => {
    setChosenTimeDelay(timeDelay);
    setSelectedTimeType(timeType);
    setSelectedTimeBeforeAfter(timeBeforeAfter);
  }, []);
  const onClose = () => {
    setOpened(false);
    setIsFetching(false);
  };
  const onAfterLeave = () => {
    setTempState(defaultValues);
    setAttachmentsTempState([]);
    setChosenSendingEmailTime(null);
    setChosenTimeDelay(0);
    setErrors(defaultErrors);
    setSelectedTimeBeforeAfter(TimeTypeState.after);
    setSelectedCourse(EventTypeEnum.registration_to_webinar);
  };
  const onChangeEmail = (value: EmailBaseInterface) => setTempState(value);
  const {
    UnsavedEmailConfirmationModal,
    removeDbRecord,
    setNeedToShowModal
  } = useUnsavedEmail({
    type: UnsavedEmailsEnum.create_funnel_email,
    currentBody: tempState?.body,
    initialBody: '',
    onPaste: data_0 => onChangeEmail({
      ...tempState,
      body: data_0
    })
  });
  return <>
      <div className={'flex justify-center px-6 py-4'}>
        <PrimaryButton onClick={() => setOpened(true)}>+ {t('global.create')}</PrimaryButton>
      </div>
      <Modal opened={opened} size={ModalSizeEnum.big} afterLeave={onAfterLeave} onClose={onClose} afterEnter={() => setNeedToShowModal(true)} title={t('dashboard.funnel.emails_edit.send.email')} isFetching={isFetching}>
        <form className="flex flex-col gap-6 lg:gap-7">
          <DashboardMailEditor sidebarItemBlock={{
          title: t('global.settings'),
          Component: renderTime(),
          showErrorOutline: !!errors.delay
        }} inModal={true} isPreFetching={false} tempState={tempState} onChangeEmail={onChangeEmail} attachmentsTempState={attachmentsTempState} setAttachmentsTempState={setAttachmentsTempState} errors={errors} additionalSubstitutes={[{
          name: t('dashboard.funnel.emails_edit.send.email_webinar.show.link'),
          slug: 'webinar_show_link'
        }, {
          name: t('dashboard.funnel.emails_edit.send.email_webinar.replay.link'),
          slug: 'webinar_replay_link'
        }]} />
          <div className="flex justify-center gap-4">
            <SecondaryButton width="large" onClick={(e_0: React.MouseEvent<HTMLButtonElement>) => {
            e_0.preventDefault();
            onClose();
          }} type="submit">
              {t('confirmation_modal.cancel')}
            </SecondaryButton>
            <PrimaryButton width="large" onClick={async (e_1: React.MouseEvent<HTMLButtonElement>) => {
            e_1.preventDefault();
            await onCreate();
          }} isFetching={isFetching} disabled={isFetching}>
              {t('global.save')}
            </PrimaryButton>
          </div>
        </form>
        {UnsavedEmailConfirmationModal}
      </Modal>
    </>;
};
export default FunnelEmailCreate;